import { create } from 'zustand';
import type { Device, DeviceGroup, FolderInput } from '@scio3/shared-types';

export type DialogType =
  | 'pair'
  | 'edit'
  | 'bulkEdit'
  | 'remove'
  | 'moveFolder'
  | 'moveOnHold'
  | 'moveTeam'
  | 'createFolder'
  | 'renameFolder'
  | 'deleteFolder'
  | 'folderPermission'
  | 'push'
  | 'execute'
  | 'preconfigure'
  | 'deviceInfo'
  | 'export'
  | 'exportDevicesList'
  | 'preview'
  | 'wifiManagement'
  | 'timezone'
  | 'kioskMode'
  | 'installVersion'
  | 'installApk'
  | 'rs232Send'
  | 'hdmiCec'
  | 'commandHistory'
  | 'remoteControl'
  | 'upgradeSubscription'
  | 'addOnConfig'
  | 'mdmDeviceInfo'
  | 'otaUpdate'
  // Platform-API room systems (Zoom / Teams / Webex / Meet) — Dashboard Issues
  // dialog wired by `MDM_VIEW_ISSUES` catalog action `{type:'open-dialog',
  // dialogId:'platformIssues'}`. Backend handler is `adapter.fetchIssues`.
  | 'platformIssues'
  | null;

interface DialogContext {
  device?: Device;
  deviceIds?: string[];
  // Full selected Device objects for bulk edit — lets BulkEditDialog run per-device add-on
  // capability checks (e.g. Video Wall unsupported on the device model/os) before assigning.
  devices?: Device[];
  folder?: DeviceGroup | FolderInput;
  groupIds?: string[];
  // Team ID of selected folders (for bulk folder moves - filters target to same team)
  selectedFoldersTeamId?: string;
  // Human-readable device type label for "upgrade your plan" error (e.g. "Webplayer", "Webplayer, Roku")
  deviceTypeLabel?: string;
  // Current folder path for operations that should inherit the folder context
  path?: string;
  contentInfo?: {
    type: string;
    id: string;
    name: string;
    fileType?: string;
    appType?: string;
    isHide?: boolean;
  };
  editSchedule?: boolean;
  // For remoteControl dialog
  remoteUrl?: string;
  // For upgradeSubscription dialog
  message?: string;
  showUpgradeButton?: boolean;
  // For addOnConfig dialog
  addOnType?: string;
  // Full folder objects for bulk delete — enables smart post-delete navigation
  folders?: DeviceGroup[];
  // Asset passed from disableCreateAsset flow (SmallApp → AddOnConfigDialog)
  asset?: Record<string, unknown>;
  // When true, EditScreenDialog auto-opens the SelectAssetModal (used for Designer return flow)
  assetPickerOpen?: boolean;
  // Pre-filled pairing code from ?code= URL deep-link (pairing.optisigns.com redirect)
  pairingCode?: string;
  // Backend error message from a failed auto-pair attempt — surfaced inline in PairScreenDialog
  pairErrorMessage?: string;
}

interface ScreenDialogState {
  activeDialog: DialogType;
  dialogContext: DialogContext;
  // Parent dialog preserved when a child dialog opens on top (e.g. MDM dialog from Edit Screen)
  parentDialog: DialogType;
  parentContext: DialogContext;
  openDialog: (dialog: DialogType, context?: DialogContext) => void;
  // Opens a child dialog while keeping the current dialog as parent.
  // When the child closes, the parent dialog is restored.
  openChildDialog: (dialog: DialogType, context?: DialogContext) => void;
  closeDialog: () => void;
}

export const useScreenDialogStore = create<ScreenDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},
  parentDialog: null,
  parentContext: {},
  openDialog: (activeDialog, dialogContext = {}) =>
    set({ activeDialog, dialogContext, parentDialog: null, parentContext: {} }),
  openChildDialog: (dialog, context = {}) =>
    set((state) => ({
      activeDialog: dialog,
      dialogContext: context,
      parentDialog: state.activeDialog,
      parentContext: state.dialogContext,
    })),
  closeDialog: () =>
    set((state) => {
      // If there's a parent dialog, restore it instead of closing everything
      if (state.parentDialog) {
        return {
          activeDialog: state.parentDialog,
          dialogContext: state.parentContext,
          parentDialog: null,
          parentContext: {},
        };
      }
      return { activeDialog: null, dialogContext: {}, parentDialog: null, parentContext: {} };
    }),
}));
